import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, AlertTriangle, XCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { subscribe, dismissCurrent, dismissAll } from "@/lib/notify-store";

const META = {
  success: { Icon: CheckCircle2, ring: "bg-emerald-500/10 text-emerald-400 ring-emerald-500/20", title: "Success" },
  error: { Icon: XCircle, ring: "bg-red-500/10 text-red-400 ring-red-500/20", title: "Error" },
  warning: { Icon: AlertTriangle, ring: "bg-amber-500/10 text-amber-400 ring-amber-500/20", title: "Notice" },
  info: { Icon: Info, ring: "bg-primary/10 text-primary ring-primary/20", title: "Info" },
};

export default function NotificationDialog() {
  const [queue, setQueue] = useState([]);
  const location = useLocation();
  useEffect(() => subscribe(setQueue), []);
  // Auto-close any open notification when the user navigates to a new
  // route so success/info dialogs don't linger over the destination page.
  useEffect(() => { dismissAll(); }, [location.pathname]);
  const current = queue[0];
  const open = !!current;
  const meta = META[current?.type] || META.info;
  const { Icon, ring, title } = meta;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismissCurrent(); }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="notification-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mx-auto mb-3 flex items-center justify-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ring-1 ${ring}`}>
              <Icon className="w-6 h-6" />
            </div>
          </div>
          <DialogTitle className="font-heading text-center" data-testid="notification-title">{title}</DialogTitle>
          <DialogDescription className="text-center text-foreground/90 whitespace-pre-line break-words" data-testid="notification-message">
            {current?.message}
          </DialogDescription>
          {current?.description && (
            <p className="text-center text-xs text-muted-foreground mt-1 whitespace-pre-line break-words" data-testid="notification-description">
              {current.description}
            </p>
          )}
        </DialogHeader>
        <DialogFooter className="sm:justify-center">
          <Button
            onClick={dismissCurrent}
            autoFocus
            className="min-w-[120px]"
            data-testid="notification-ok-btn"
          >
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
